import type { WorldEventHandler, WorldEventContext } from '../types';
import { EventSocialPass } from '../types';
import type {
  WorldEventStartPayload,
  WorldEventLinePayload,
  WorldEventEndPayload,
  SocialPassConfig,
  NearbyHeroData,
} from '../../game/types';
import { registerWorldEventHandler } from '../registry';
import { adventureLogTemplate } from '../../i18n/loadLocales';

function normalizeHeroModelVariant(v: unknown): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 2 ? v : 0;
}

function onStart(payload: WorldEventStartPayload, ctx: WorldEventContext): void {
  const partner = payload.partner;
  if (!partner) return;
  const config = (payload.config ?? {}) as SocialPassConfig;
  const phase = payload.phase;
  const showChat = phase === 'meet' || phase == null;

  const overlay: NearbyHeroData = {
    id: partner.id,
    name: partner.name,
    level: partner.level,
    modelVariant: normalizeHeroModelVariant(partner.modelVariant),
    positionX: partner.positionX,
    positionY: partner.positionY,
  };
  const engine = ctx.engine();
  if (engine) engine.setWorldEventPartnerOverlay(overlay);

  ctx.setActive({
    kind: 'socialPass',
    eventId: EventSocialPass,
    sessionId: payload.sessionId,
    partnerName: partner.name,
    anySideOnline: config.anySideOnline ?? false,
    maxChars: 140,
    chatVisible: showChat,
  });
}

function onLine(payload: WorldEventLinePayload, ctx: WorldEventContext): void {
  const engine = ctx.engine();
  if (!engine) return;
  const { locale } = ctx.locale();
  let text = (payload.text ?? '').trim();
  if (payload.fromKind === 'scripted' && payload.lineKey) {
    text = (adventureLogTemplate(locale, payload.lineKey) ?? payload.lineKey).trim();
  }
  if (!text) return;
  engine.applyWorldEventChatLine(payload.fromHeroId ?? 0, payload.fromKind, text);
}

function onEnd(payload: WorldEventEndPayload, ctx: WorldEventContext): void {
  const active = ctx.getActive();
  if (active?.eventId !== EventSocialPass || active.sessionId !== payload.sessionId) return;
  ctx.setActive(null);
  const engine = ctx.engine();
  if (engine) {
    const linger = payload.lingerMs != null && payload.lingerMs > 0 ? payload.lingerMs : 20000;
    engine.endWorldEventPartnerOverlayLinger(linger);
  }
}

const handler: WorldEventHandler = {
  eventId: EventSocialPass,
  onStart,
  onLine,
  onEnd,
};

registerWorldEventHandler(handler);

export default handler;
